import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  AdminNavItem,
  HelpNavItem,
  MyZoneNavItem,
  NavItemPrimaryInterface,
  NavItemSecondaryInterface,
  ReportsNavItem,
  UsersNavItem,
} from "app/app-layout/models";
import { AppStateInterface } from "app/core/app-config/state/models";
import { AuthService } from "app/core/authentication/services";
import { FeatureInterface } from "app/core/feature-control/models";
import { FeatureStateInterface } from "app/core/feature-control/state/models";
import * as jwtDecode from "jwt-decode";
import { Observable, of } from "rxjs";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";

import { TrainingNavItem } from "../models/nav/nav-items/training-nav-items.model";

import {
  NavItemsActionType,
  NavItemsLoad,
  NavItemsLoadCompleted,
  NavItemsLoadFailed,
} from "./actions";

@Injectable()
export class NavItemsEffectsService {
  /**
   * Load Navigation items from the provided token scopes
   */
  @Effect()
  public loadNavItems$: Observable<Action> = this._actions$.pipe(
    ofType(NavItemsActionType.LoadNavItems),
    map((action: NavItemsLoad) => action.payload),
    switchMap((jwt: string) => {
      // Wait for features to be loaded
      return this._store
        .select(
          ({ featureControl }: { featureControl: FeatureStateInterface }) =>
            featureControl,
        )
        .pipe(
          filter(
            (featureState: FeatureStateInterface) =>
              featureState && featureState.loaded === true,
          ),
          take(1),
          map((featureState: FeatureStateInterface) => ({
            jwt,
            features: featureState.features || [],
          })),
        );
    }),
    map(({ jwt, features }: { jwt: string; features: FeatureInterface[] }) => {
      const decryptedToken: any = jwtDecode(jwt);
      const scopes: string[] = JSON.parse(decryptedToken.scopes);

      // Start with all possible navigation elements and filter based on token scopes.
      const primaryNavItems: Readonly<NavItemPrimaryInterface[]> = [
        new MyZoneNavItem(),
        new ReportsNavItem(),
        new UsersNavItem(),
        new TrainingNavItem(),
        new AdminNavItem(),
        new HelpNavItem(),
      ];

      const filteredItems: NavItemPrimaryInterface[] = primaryNavItems.filter(
        (item: NavItemPrimaryInterface) => {
          if (!item.menuItems || item.menuItems.length === 0) {
            return false;
          }

          // Filter out any secondaries the user doesn't have permissions to see
          // and also filter by feature flags
          item.menuItems = item.menuItems.filter(
            (secondaryItem: NavItemSecondaryInterface) => {
              // First check scope authorization
              const isAuthorized = AuthService.isTokenAuthorized(
                scopes,
                secondaryItem.scopes,
                secondaryItem.scopeMatch,
              );
              if (!isAuthorized) {
                return false;
              }

              // Then check feature flag if one is defined
              if (secondaryItem.featureCode) {
                const feature = features.find(
                  (f: FeatureInterface) =>
                    f.featureCode === secondaryItem.featureCode,
                );
                // If feature is not found or not active, hide the nav item
                if (!feature || !feature.isActive) {
                  return false;
                }
              }

              return true;
            },
          );

          // Only return items that have secondaries.
          return item.menuItems.length > 0;
        },
      );

      return filteredItems;
    }),
    map((items: NavItemPrimaryInterface[]) => new NavItemsLoadCompleted(items)),
    catchError((err: any) => of(new NavItemsLoadFailed(err))),
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppStateInterface>,
  ) {}
}
